import { Injectable, signal } from '@angular/core';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut } from 'aws-amplify/auth';

export interface UserInfo {
	firstName: string | undefined,
	lastName: string | undefined,
	email: string | undefined
}

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private readonly _loggedIn = signal<boolean>(false);
	public readonly loggedIn = this._loggedIn.asReadonly();

	private readonly _userInfo = signal<UserInfo | undefined>(undefined);
	public readonly userInfo = this._userInfo.asReadonly();
	
	constructor() { }

	public async refreshAuthState(): Promise<void> {
		try {
			await getCurrentUser();
			this._loggedIn.set(true);

			const attrs = await fetchUserAttributes();
			this._userInfo.set({
				firstName: attrs.name,
				lastName: attrs.family_name,
				email: attrs.email
			});
		} catch {
			this._loggedIn.set(false);
			this._userInfo.set(undefined);
		}
	}

	async isLoggedIn(): Promise<boolean> {
		try {
			await getCurrentUser();
			return true;
		}
		catch {
			return false;
		}
	}

	async getUserId(): Promise<string | undefined> {
		try {
			return (await getCurrentUser()).userId;
		}
		catch {
			return undefined;
		}
	}

	async getToken(): Promise<string | undefined> {
		try {
			return (await fetchAuthSession()).tokens?.accessToken?.toString();
		}
		catch {
			return undefined;
		}
	}

	async getUserInfo(): Promise<UserInfo | undefined> {
		try {
			const userAttributes = await fetchUserAttributes()
			return {
				firstName: userAttributes.name,
				lastName: userAttributes.family_name,
				email: userAttributes.email
			};
		} catch {
			return undefined;
		}
	}

	async getUserGroups(): Promise<string[] | undefined> {
		try {
			const session = await fetchAuthSession();
			const idToken = session.tokens?.idToken;
			if (idToken?.payload['cognito:groups']) {
				return idToken.payload['cognito:groups'] as string[];
			}
			return undefined;
		} catch {
			return undefined;
		}
	}

	async isInstaller(): Promise<boolean> {
		try {
			const groups = await this.getUserGroups();
			if (groups && groups.includes('Installer')) {
				console.log('isInstaller(): user is Installer');
				return true;
			}
			return false;
		}
		catch {
			return false;
		}
	}

	async isInstallerDSO(): Promise<boolean> {
		try {
			const groups = await this.getUserGroups();
			if (groups && groups.includes('DSO_Installer')) {
				console.log('isInstallerDSO(): user is DSO_Installer');
				return true;
			}
			return false;
		}
		catch {
			return false;
		}
	}

	async logOut() {
		await signOut();
		this._loggedIn.set(false);
		this._userInfo.set(undefined);
	}
}

